import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchVisitasStats, fetchVisitasStatsPeriod, fetchVisitasAgentStats, fetchActiveSellers } from '../../Redux/Actions/actions';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  RadialLinearScale, ArcElement, BarElement,
  Tooltip, Legend, Filler
} from 'chart.js';
import { Radar, Line, Doughnut } from 'react-chartjs-2';
import styles from './estadisticas.module.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  RadialLinearScale, ArcElement, BarElement,
  Tooltip, Legend, Filler
);

const getSegmentClass = (seg) => {
  const map = { excelente: 'badgeExcelente', buena: 'badgeBuena', regular: 'badgeRegular', critica: 'badgeCritica' };
  return map[seg] || 'badgeRegular';
};

const getSegmentFromValue = (v) => {
  if (v >= 85) return 'excelente';
  if (v >= 70) return 'buena';
  if (v >= 55) return 'regular';
  return 'critica';
};

const breakdownColors = {
  excelente: '#27AE60', muySatisfactorio: '#27AE60', yes: '#27AE60',
  buena: '#2E2F8F', muyBuena: '#2E2F8F', satisfactorio: '#2E2F8F',
  regular: '#E67E22', maybe: '#E67E22',
  mala: '#E74C3C', insatisfactorio: '#E74C3C', no: '#E74C3C'
};

const HelpBox = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className={styles.helpToggle} onClick={() => setOpen(o => !o)} type="button">
        ℹ️ {open ? 'Ocultar ayuda' : '¿Qué significa esto?'}
        <span className={`${styles.helpToggleIcon} ${open ? styles.helpToggleIconOpen : ''}`}>▼</span>
      </button>
      {open && <div className={styles.helpDescription}>{children}</div>}
    </>
  );
};

const VisitasStats = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { visitasStats, visitasStatsPeriod, visitasAgentStats, activeSellers, estadisticasLoading, estadisticasError } = useSelector(s => s);

  const [filters, setFilters] = useState({
    startDate: '', endDate: '', top: '10', minVisitsForRanking: '1', comparePrevious: true
  });
  const [usePeriod, setUsePeriod] = useState(false);
  const [activeTab, setActiveTab] = useState('propertiesByVisits');
  const [segmentModal, setSegmentModal] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [agentSearched, setAgentSearched] = useState('');

  const sellersList = activeSellers?.data || activeSellers || [];

  const loadAgentStats = useCallback((agentName) => {
    const name = agentName || selectedAgent;
    if (!name) return;
    setAgentSearched(name);
    dispatch(fetchVisitasAgentStats(name, {
      top: filters.top,
      minVisitsForRanking: filters.minVisitsForRanking
    }));
  }, [dispatch, selectedAgent, filters.top, filters.minVisitsForRanking]);

  const loadData = useCallback(() => {
    if (filters.startDate && filters.endDate) {
      setUsePeriod(true);
      dispatch(fetchVisitasStatsPeriod({
        startDate: filters.startDate,
        endDate: filters.endDate,
        top: filters.top,
        minVisitsForRanking: filters.minVisitsForRanking,
        comparePrevious: filters.comparePrevious
      }));
    } else {
      setUsePeriod(false);
      dispatch(fetchVisitasStats({
        top: filters.top,
        minVisitsForRanking: filters.minVisitsForRanking
      }));
    }
  }, [dispatch, filters]);

  useEffect(() => { loadData(); dispatch(fetchActiveSellers()); }, []);

  const rawData = usePeriod ? visitasStatsPeriod : visitasStats;
  const data = usePeriod ? rawData?.data?.current : rawData?.data;
  const comparison = usePeriod ? rawData?.data?.comparison : null;
  const visitasExternasStats = rawData?.visitasExternas || null;
  const resumenGeneral = rawData?.resumenGeneral || null;

  if (estadisticasLoading) {
    return <div className={styles.loadingContainer}><div className={styles.spinner} /><span>Cargando estadísticas de visitas...</span></div>;
  }

  if (estadisticasError) {
    return <div className={styles.errorContainer}><span>⚠️ {estadisticasError}</span><button className={styles.retryButton} onClick={loadData}>Reintentar</button></div>;
  }

  if (!data) {
    return <div className={styles.emptyState}><span className={styles.emptyIcon}>📊</span><span>No hay datos disponibles</span></div>;
  }

  const meta = data.metadata || {};
  const global = data.global || {};
  const prom = global.promedios || {};
  const funnel = global.funnel || {};
  const insights = global.insights || {};
  const temporal = global.temporal || {};
  const rankings = data.rankings || {};
  const distributions = data.distributions || {};
  const breakdown = global.breakdown || {};
  const detail = data.detail || {};

  // === Visitas Externas data ===
  const extInteres = visitasExternasStats?.interes || {};
  const extPorAgente = visitasExternasStats?.porAgente || [];
  const extPorMes = visitasExternasStats?.porMes || [];

  // === KPI Cards ===
  const kpis = [
    { label: 'Total visitas (internas)', value: meta.totalVisitas || 0, icon: '📊', color: '#2E2F8F' },
    { label: 'Visitas externas', value: resumenGeneral?.totalVisitasExternas || visitasExternasStats?.total || 0, icon: '🌐', color: '#8B2252' },
    { label: 'Total global', value: resumenGeneral?.totalVisitasGlobal || (meta.totalVisitas || 0), icon: '📈', color: '#1A1A3E' },
    { label: 'Propiedades visitadas', value: meta.totalPropiedadesConVisitas || 0, icon: '🏠', color: '#2E2F8F' },
    { label: 'Approval Rate', value: `${(prom.approvalRate || 0).toFixed(1)}%`, icon: '⭐', color: '#2E2F8F', segment: getSegmentFromValue(prom.approvalRate || 0) },
    { label: 'Intención compra', value: `${(prom.promedioComprar || 0).toFixed(1)}%`, icon: '🎯', color: '#8B2252', segment: getSegmentFromValue(prom.promedioComprar || 0) },
  ];

  // === Funnel Data ===
  const funnelRows = [
    { label: 'Total visitas', count: funnel.totalVisitas || 0, rate: 100, color: '#2E2F8F' },
    { label: 'Les gustó', count: funnel.gustoPositivo?.count || 0, rate: funnel.gustoPositivo?.rate || 0, color: '#3D3FAD' },
    { label: 'Intención compra', count: funnel.intencionCompraDirecta?.count || 0, rate: funnel.intencionCompraDirecta?.rate || 0, color: '#27AE60' },
    { label: 'Interesados alternativas', count: funnel.interesadosEnAlternativas?.count || 0, rate: funnel.interesadosEnAlternativas?.rate || 0, color: '#C9A84C' },
  ];

  // === Radar Data ===
  const radarLabels = ['Estado', 'Ubicación', 'Espacios', 'Cal/Precio', 'General', 'Comprar', 'Ver otras'];
  const radarValues = [
    prom.promedioEstado || 0, prom.promedioUbicacion || 0, prom.promedioEspacios || 0,
    prom.promedioCalidadPrecio || 0, prom.promedioGeneral || 0, prom.promedioComprar || 0, prom.promedioVerOtras || 0
  ];
  const radarData = {
    labels: radarLabels,
    datasets: [{
      label: 'Promedio',
      data: radarValues,
      backgroundColor: 'rgba(46, 47, 143, 0.12)',
      borderColor: '#2E2F8F',
      borderWidth: 2,
      pointRadius: 4,
      pointBackgroundColor: '#2E2F8F'
    }]
  };
  const radarOpts = {
    responsive: true,
    scales: { r: { min: 0, max: 100, ticks: { stepSize: 20 }, grid: { color: '#E0E0E8' }, pointLabels: { font: { size: 11 } } } },
    plugins: { legend: { display: false } }
  };

  // === Temporal Line Chart ===
  const months = (temporal.byMonth || []).map(m => m.month);

  // Build externas por mes lookup
  const extMesMap = {};
  extPorMes.forEach(m => { extMesMap[m.mes] = m.cantidad; });

  const lineData = {
    labels: months,
    datasets: [
      {
        label: 'Approval Rate',
        data: (temporal.byMonth || []).map(m => m.approvalRate),
        borderColor: '#2E2F8F',
        backgroundColor: 'rgba(46, 47, 143, 0.08)',
        fill: true,
        tension: 0.3,
        borderWidth: 2.5,
        yAxisID: 'y'
      },
      {
        label: 'Visitas Internas',
        data: (temporal.byMonth || []).map(m => m.totalVisitas),
        borderColor: '#8B2252',
        borderDash: [4, 3],
        fill: false,
        tension: 0.3,
        borderWidth: 2,
        yAxisID: 'y1'
      },
      {
        label: 'Visitas Externas',
        data: months.map(m => extMesMap[m] || 0),
        borderColor: '#C9A84C',
        borderDash: [6, 3],
        fill: false,
        tension: 0.3,
        borderWidth: 2,
        yAxisID: 'y1'
      }
    ]
  };
  const lineOpts = {
    responsive: true,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: { beginAtZero: true, max: 100, grid: { color: '#F0F0F8' }, title: { display: true, text: 'Approval Rate (%)' } },
      y1: { position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, title: { display: true, text: 'Visitas' } },
      x: { grid: { display: false } }
    },
    plugins: { legend: { position: 'bottom' } }
  };

  // === Donut: Distributions ===
  const segColors = ['#27AE60', '#2E2F8F', '#E67E22', '#E74C3C'];
  const segLabels = ['excelente', 'buena', 'regular', 'critica'];
  const propsApproval = distributions.propertiesByApprovalSegment || {};
  const sellersApproval = distributions.sellersByApprovalSegment || {};

  const donutPropsData = {
    labels: segLabels,
    datasets: [{ data: segLabels.map(s => propsApproval[s] || 0), backgroundColor: segColors, borderWidth: 0, spacing: 2 }]
  };
  const donutSellersData = {
    labels: segLabels,
    datasets: [{ data: segLabels.map(s => sellersApproval[s] || 0), backgroundColor: segColors, borderWidth: 0, spacing: 2 }]
  };
  const donutOpts = { responsive: true, cutout: '65%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } } } };

  const segColorMap = { excelente: '#27AE60', buena: '#2E2F8F', regular: '#E67E22', critica: '#E74C3C' };

  const handlePropsDonutClick = (_, elements) => {
    if (!elements.length) return;
    const seg = segLabels[elements[0].index];
    const items = (detail.properties || []).filter(p => p.segmento === seg);
    setSegmentModal({ type: 'properties', segment: seg, items, color: segColorMap[seg] });
  };

  const handleSellersDonutClick = (_, elements) => {
    if (!elements.length) return;
    const seg = segLabels[elements[0].index];
    const items = (detail.sellers || []).filter(s => s.segmento === seg);
    setSegmentModal({ type: 'sellers', segment: seg, items, color: segColorMap[seg] });
  };

  // === Ranking tabs ===
  const rankingTabs = [
    { key: 'propertiesByVisits', label: 'Top Props (visitas)' },
    { key: 'propertiesByApproval', label: 'Top Props (aprobación)' },
    { key: 'lowestApprovalProperties', label: 'Props críticas' },
    { key: 'sellersByVisits', label: 'Top Agentes (visitas)' },
    { key: 'sellersByApproval', label: 'Top Agentes (aprobación)' },
  ];

  const isPropertyTab = activeTab.toLowerCase().includes('properties') || activeTab === 'lowestApprovalProperties';
  const rankingData = rankings[activeTab] || [];

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div className={styles.dashboardContainer}>
      {/* Header */}
      <div className={styles.dashboardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button className={styles.backButton} onClick={() => navigate('/estadisticas')}>← Volver</button>
          <h1 className={styles.dashboardTitle}>Estadísticas de Visitas</h1>
        </div>
      </div>

      {/* V9: Filtros */}
      <div className={styles.filtersBar}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Fecha inicio</label>
          <input type="date" className={styles.filterInputDate} value={filters.startDate} onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))} />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Fecha fin</label>
          <input type="date" className={styles.filterInputDate} value={filters.endDate} onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))} />
        </div>
        <div className={styles.filterSecondary}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Top</label>
            <input type="number" className={styles.filterInputSmall} value={filters.top} onChange={e => setFilters(p => ({ ...p, top: e.target.value }))} min={1} />
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Mín. visitas ranking</label>
            <input type="number" className={styles.filterInputSmall} value={filters.minVisitsForRanking} onChange={e => setFilters(p => ({ ...p, minVisitsForRanking: e.target.value }))} min={1} />
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Comparar anterior</label>
            <div className={styles.filterToggleWrap}>
              <label className={styles.toggleSwitch}>
                <input type="checkbox" checked={filters.comparePrevious} onChange={e => setFilters(p => ({ ...p, comparePrevious: e.target.checked }))} />
                <span className={styles.toggleSlider}></span>
              </label>
            </div>
          </div>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Agente</label>
          <select
            className={styles.agentSelect}
            value={selectedAgent}
            onChange={e => {
              setSelectedAgent(e.target.value);
              if (e.target.value) loadAgentStats(e.target.value);
            }}
          >
            <option value="">Todos los agentes</option>
            {sellersList.map(s => {
              const fullName = `${s.name} ${s.last_name || s.lastName || ''}`.trim();
              return <option key={s.id} value={fullName}>{fullName}</option>;
            })}
          </select>
        </div>
        <div className={styles.filterButtons}>
          <button className={styles.filterButton} onClick={loadData}>Aplicar filtros</button>
          <button className={styles.clearButton} onClick={() => { setFilters({ startDate: '', endDate: '', top: '10', minVisitsForRanking: '1', comparePrevious: true }); setUsePeriod(false); setSelectedAgent(''); setAgentSearched(''); dispatch(fetchVisitasStats({ top: '10', minVisitsForRanking: '1' })); }}>Limpiar</button>
        </div>
      </div>

      {/* V8: Comparativa entre períodos */}
      {comparison && comparison.deltas && (
        <>
        <div className={styles.sectionTitleRow}>
          <h3 className={styles.sectionTitle}>Comparativa entre Períodos</h3>
          <HelpBox>
            <p>Muestra la variación de métricas clave respecto al período anterior de igual duración:</p>
            <ul>
              <li><strong>Δ Visitas:</strong> Cambio en la cantidad total de visitas.</li>
              <li><strong>Δ Approval Rate:</strong> Cambio en el porcentaje de aprobación.</li>
              <li><strong>Δ Promedio General:</strong> Cambio en la calificación general promedio.</li>
            </ul>
            <p>💡 Flechas verdes (↑) indican mejora, rojas (↓) indican deterioro respecto al período previo.</p>
          </HelpBox>
        </div>
        <div className={styles.deltaGrid}>
          {[
            { label: 'Δ Visitas (internas)', value: comparison.deltas.totalVisitas },
            { label: 'Δ Visitas Externas', value: comparison.deltas.totalVisitasExternas },
            { label: 'Δ Approval Rate', value: comparison.deltas.approvalRate, suffix: '%' },
            { label: 'Δ Promedio General', value: comparison.deltas.promedioGeneralFinal },
          ].filter(d => d.value != null).map((d, i) => (
            <div key={i} className={styles.deltaCard} style={{ borderLeftColor: d.value >= 0 ? '#27AE60' : '#E74C3C' }}>
              <div className={styles.deltaLabel}>{d.label}</div>
              <div className={styles.deltaValue} style={{ color: d.value >= 0 ? '#27AE60' : '#E74C3C' }}>
                {d.value >= 0 ? '↑' : '↓'} {d.value >= 0 ? '+' : ''}{typeof d.value === 'number' ? d.value.toFixed(1) : d.value}{d.suffix || ''}
              </div>
              <div className={styles.deltaSub}>vs {comparison.previousPeriod?.startDate} a {comparison.previousPeriod?.endDate}</div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* V1: KPI Cards */}
      <div className={styles.sectionTitleRow}>
        <h3 className={styles.sectionTitle}>Indicadores Clave</h3>
        <HelpBox>
          <p>Estos indicadores resumen el rendimiento general de las visitas:</p>
          <ul>
            <li><strong>Total visitas (internas):</strong> Visitas a propiedades de la inmobiliaria.</li>
            <li><strong>Visitas externas:</strong> Visitas a propiedades de terceros (otras inmobiliarias o particulares).</li>
            <li><strong>Total global:</strong> Suma de internas + externas.</li>
            <li><strong>Propiedades visitadas:</strong> Propiedades internas distintas con al menos una visita.</li>
            <li><strong>Approval Rate:</strong> Porcentaje promedio de aprobación (solo internas). Verde ≥85%, azul ≥70%, naranja ≥55%, rojo &lt;55%.</li>
            <li><strong>Intención de compra:</strong> Porcentaje promedio de visitantes que manifestaron intención de compra directa (solo internas).</li>
          </ul>
        </HelpBox>
      </div>
      <div className={styles.kpiGrid}>
        {kpis.map((k, i) => (
          <div key={i} className={styles.kpiCard}>
            <div className={styles.kpiIcon} style={{ background: `${k.color}15` }}>{k.icon}</div>
            <span className={styles.kpiLabel}>{k.label}</span>
            <span className={styles.kpiValue}>{k.value}</span>
            {k.segment && <span className={`${styles.badge} ${styles[getSegmentClass(k.segment)]}`}>{k.segment}</span>}
          </div>
        ))}
      </div>

      {/* V2: Funnel */}
      <div className={styles.section}>
        <div className={styles.sectionTitleRow}>
          <h3 className={styles.sectionTitle}>Funnel de Conversión</h3>
          <HelpBox>
            <p>El funnel muestra cómo se reducen los visitantes en cada etapa del proceso de decisión:</p>
            <ul>
              <li><strong>Total visitas:</strong> Todas las visitas realizadas (100%).</li>
              <li><strong>Les gustó:</strong> Visitantes que calificaron positivamente la propiedad.</li>
              <li><strong>Intención compra:</strong> Visitantes que expresaron interés real en comprar.</li>
              <li><strong>Interesados en alternativas:</strong> Visitantes que pidieron ver otras propiedades similares.</li>
            </ul>
            <p>💡 <strong>Cómo usarlo:</strong> Si hay una gran caída entre "Les gustó" e "Intención compra", las propiedades gustan pero el precio o condiciones frenan la decisión. Revisa la relación calidad/precio.</p>
          </HelpBox>
        </div>
        <div className={styles.funnelContainer}>
          {funnelRows.map((r, i) => (
            <div key={i} className={styles.funnelRow}>
              <span className={styles.funnelLabel}>{r.label}</span>
              <div className={styles.funnelBarBg}>
                <div className={styles.funnelBar} style={{ width: `${r.rate}%`, background: r.color }}>
                  {r.count}
                </div>
              </div>
              <span className={styles.funnelValue}>{r.rate.toFixed(1)}%</span>
            </div>
          ))}
        </div>
        {funnel.intencionCompraDirecta?.conversionFromGustoPositivo != null && (
          <div className={styles.conversionBadge}>
            🔄 Conversión gusto→compra: {funnel.intencionCompraDirecta.conversionFromGustoPositivo.toFixed(1)}%
          </div>
        )}
      </div>

      {/* V3: Radar + V4: Temporal */}
      <div className={styles.chartRow}>
        <div className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>Radar de Dimensiones</h3>
            <HelpBox>
              <p>El radar muestra la calificación promedio (0-100) en cada dimensión evaluada por los visitantes:</p>
              <ul>
                <li><strong>Estado:</strong> Condición física y mantenimiento de la propiedad.</li>
                <li><strong>Ubicación:</strong> Calificación del barrio, accesos y entorno.</li>
                <li><strong>Espacios:</strong> Distribución y tamaño de ambientes.</li>
                <li><strong>Cal/Precio:</strong> Percepción de la relación calidad-precio.</li>
                <li><strong>General:</strong> Impresión general de la visita.</li>
                <li><strong>Comprar:</strong> Intención de compra del visitante.</li>
                <li><strong>Ver otras:</strong> Interés en ver propiedades alternativas.</li>
              </ul>
              <p>💡 <strong>Cómo usarlo:</strong> Identifica dimensiones con valores bajos para enfocar mejoras. Si "Cal/Precio" es bajo pero las demás son altas, el precio puede estar fuera de mercado.</p>
            </HelpBox>
          </div>
          <div className={styles.chartContainer}>
            <Radar data={radarData} options={radarOpts} />
          </div>
          <div className={styles.insightsBox}>
            {insights.strongestDimension && (
              <span className={`${styles.insightItem} ${styles.trendBadge} ${styles.trendUp}`}>
                💪 Más fuerte: {insights.strongestDimension.key} ({insights.strongestDimension.value?.toFixed(1)})
              </span>
            )}
            {insights.weakestDimension && (
              <span className={`${styles.insightItem} ${styles.trendBadge} ${styles.trendDown}`}>
                ⚠️ Más débil: {insights.weakestDimension.key} ({insights.weakestDimension.value?.toFixed(1)})
              </span>
            )}
            {insights.marketTemperature && (
              <span className={`${styles.badge} ${styles[getSegmentClass(getSegmentFromValue(prom.approvalRate || 0))]}`}>
                🌡️ Mercado: {insights.marketTemperature}
              </span>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>Evolución Temporal</h3>
            <HelpBox>
              <p>Este gráfico de líneas muestra cómo evolucionan dos métricas clave mes a mes:</p>
              <ul>
                <li><strong>Approval Rate (línea azul, eje izq.):</strong> Porcentaje de aprobación promedio mensual. Indica la satisfacción general con las visitas.</li>
                <li><strong>Total Visitas (línea roja punteada, eje der.):</strong> Cantidad de visitas realizadas cada mes.</li>
              </ul>
              <p>💡 <strong>Cómo usarlo:</strong> Busca tendencias ascendentes o descendentes. Si las visitas bajan pero el approval sube, puede indicar mejor calificación del público. Si ambas bajan, puede haber un problema de mercado o de cartera.</p>
            </HelpBox>
          </div>
          {temporal.trend && (
            <div style={{ textAlign: 'right', marginBottom: 8 }}>
              <span className={`${styles.trendBadge} ${temporal.trend.deltaApprovalRate >= 0 ? styles.trendUp : styles.trendDown}`}>
                {temporal.trend.deltaApprovalRate >= 0 ? '↑' : '↓'} vs mes anterior: {temporal.trend.deltaApprovalRate?.toFixed(1)}%
              </span>
            </div>
          )}
          <div className={styles.chartContainer}>
            {months.length > 0 ? <Line data={lineData} options={lineOpts} /> : <div className={styles.emptyState}>Sin datos temporales</div>}
          </div>
        </div>
      </div>

      {/* V5: Distribución por Segmento (Donuts) */}
      <div className={styles.chartRow}>
        <div className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>Propiedades por Segmento</h3>
            <HelpBox>
              <p>Este gráfico circular clasifica las propiedades según su nivel de aprobación:</p>
              <ul>
                <li><strong style={{color:'#27AE60'}}>Excelente (≥85%):</strong> Propiedades muy bien valoradas.</li>
                <li><strong style={{color:'#2E2F8F'}}>Buena (70-84%):</strong> Propiedades con buena recepción.</li>
                <li><strong style={{color:'#E67E22'}}>Regular (55-69%):</strong> Propiedades que necesitan atención.</li>
                <li><strong style={{color:'#E74C3C'}}>Crítica (&lt;55%):</strong> Propiedades con mala recepción, requieren acción inmediata.</li>
              </ul>
              <p>💡 <strong>Cómo usarlo:</strong> Si muchas propiedades están en "crítica" o "regular", revisa sus precios, fotos o estado para mejorar la percepción.</p>
            </HelpBox>
          </div>
          <div className={styles.donutWrapper}>
            <div style={{ maxWidth: 280, margin: '0 auto', cursor: 'pointer' }}>
              <Doughnut data={donutPropsData} options={{ ...donutOpts, onClick: handlePropsDonutClick }} />
            </div>
            <p style={{ textAlign: 'center', fontSize: 11, color: '#6B6B8A', marginTop: 8 }}>Haz clic en un segmento para ver el detalle</p>
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>Agentes por Segmento</h3>
            <HelpBox>
              <p>Similar al gráfico anterior, pero clasifica a los <strong>agentes inmobiliarios</strong> según el approval rate promedio de sus propiedades visitadas:</p>
              <ul>
                <li><strong style={{color:'#27AE60'}}>Excelente:</strong> Agentes cuyas propiedades reciben calificaciones sobresalientes.</li>
                <li><strong style={{color:'#E74C3C'}}>Crítica:</strong> Agentes que necesitan mejorar la presentación o selección de propiedades.</li>
              </ul>
              <p>💡 <strong>Cómo usarlo:</strong> Identifica agentes con bajo rendimiento para ofrecer capacitación o acompañamiento.</p>
            </HelpBox>
          </div>
          <div className={styles.donutWrapper}>
            <div style={{ maxWidth: 280, margin: '0 auto', cursor: 'pointer' }}>
              <Doughnut data={donutSellersData} options={{ ...donutOpts, onClick: handleSellersDonutClick }} />
            </div>
            <p style={{ textAlign: 'center', fontSize: 11, color: '#6B6B8A', marginTop: 8 }}>Haz clic en un segmento para ver el detalle</p>
          </div>
        </div>
      </div>

      {/* V7: Breakdown por Pregunta */}
      {Object.keys(breakdown).length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>Breakdown por Pregunta</h3>
            <HelpBox>
              <p>Este panel desglosa las respuestas de cada pregunta del formulario de visita:</p>
              <ul>
                <li>Cada barra muestra la distribución porcentual de las respuestas posibles (excelente, buena, regular, mala, sí, no, etc.).</li>
                <li><strong>Cob (Cobertura):</strong> Porcentaje de visitas que respondieron esa pregunta. Un valor bajo indica que muchos visitantes no completaron esa sección.</li>
              </ul>
              <p>💡 <strong>Cómo usarlo:</strong> Identifica preguntas donde predominan respuestas negativas para detectar problemas específicos. Si la cobertura es baja, considera simplificar el formulario.</p>
            </HelpBox>
          </div>
          <div className={styles.breakdownContainer}>
            {Object.entries(breakdown).map(([key, val]) => {
              const opts = val.options || {};
              return (
                <div key={key} className={styles.breakdownRow}>
                  <span className={styles.breakdownLabel}>{key}</span>
                  <div className={styles.breakdownBarBg}>
                    {Object.entries(opts).map(([optKey, optVal]) => {
                      const pct = optVal.percentageOverAnswers || 0;
                      return pct > 0 ? (
                        <div key={optKey} className={styles.breakdownSegment}
                          style={{ width: `${pct}%`, background: breakdownColors[optKey] || '#6B6B8A' }}
                          title={`${optKey}: ${optVal.count} (${pct.toFixed(1)}%)`}>
                          {pct > 8 ? `${pct.toFixed(0)}%` : ''}
                        </div>
                      ) : null;
                    })}
                  </div>
                  <span className={styles.breakdownCoverage}>Cob: {val.answerCoverage?.toFixed(0) || 0}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* V11: Visitas Externas */}
      {visitasExternasStats && visitasExternasStats.total > 0 && (
        <div className={styles.section} style={{ borderTop: '3px solid #C9A84C' }}>
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>🌐 Visitas Externas</h3>
            <HelpBox>
              <p>Visitas realizadas a propiedades que <strong>no pertenecen a la inmobiliaria</strong>. Son registros manuales donde el agente lleva a un comprador a ver propiedades de terceros.</p>
              <ul>
                <li><strong>Interés del comprador:</strong> Distribución de respuestas: Le interesa, No le interesa, Quizás.</li>
                <li><strong>Por agente:</strong> Cantidad de visitas externas que realizó cada agente.</li>
                <li><strong>Por mes:</strong> La tendencia mensual se muestra integrada en el gráfico de Evolución Temporal (línea dorada).</li>
              </ul>
            </HelpBox>
          </div>

          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiIcon} style={{ background: '#C9A84C15' }}>🌐</div>
              <span className={styles.kpiLabel}>Total externas</span>
              <span className={styles.kpiValue}>{visitasExternasStats.total}</span>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiIcon} style={{ background: '#27AE6015' }}>👍</div>
              <span className={styles.kpiLabel}>Les interesa</span>
              <span className={styles.kpiValue}>{extInteres.yes || 0}</span>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiIcon} style={{ background: '#E74C3C15' }}>👎</div>
              <span className={styles.kpiLabel}>No les interesa</span>
              <span className={styles.kpiValue}>{extInteres.no || 0}</span>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiIcon} style={{ background: '#E67E2215' }}>🤔</div>
              <span className={styles.kpiLabel}>Quizás</span>
              <span className={styles.kpiValue}>{extInteres.maybe || 0}</span>
            </div>
          </div>

          <div className={styles.chartRow}>
            {/* Donut de interés */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Interés del Comprador</h4>
              <div className={styles.donutWrapper}>
                <div style={{ maxWidth: 260, margin: '0 auto' }}>
                  <Doughnut
                    data={{
                      labels: ['Le interesa', 'No le interesa', 'Quizás'],
                      datasets: [{
                        data: [extInteres.yes || 0, extInteres.no || 0, extInteres.maybe || 0],
                        backgroundColor: ['#27AE60', '#E74C3C', '#E67E22'],
                        borderWidth: 0,
                        spacing: 2
                      }]
                    }}
                    options={{ responsive: true, cutout: '65%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } } } }}
                  />
                </div>
              </div>
            </div>

            {/* Tabla por agente */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Externas por Agente</h4>
              {extPorAgente.length > 0 ? (
                <div className={styles.tableContainer}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Agente</th>
                        <th>Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extPorAgente.map((item, i) => (
                        <tr key={i}>
                          <td><span className={styles.medal}>{medals[i] || i + 1}</span></td>
                          <td style={{ fontWeight: 500 }}>{item.agente}</td>
                          <td style={{ fontWeight: 700 }}>{item.cantidad}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.emptyState}>Sin datos por agente</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* V6: Rankings */}
      <div className={styles.section}>
        <div className={styles.sectionTitleRow}>
          <h3 className={styles.sectionTitle}>Rankings</h3>
          <HelpBox>
            <p>Las tablas de ranking permiten comparar propiedades y agentes según distintos criterios:</p>
            <ul>
              <li><strong>Top Props (visitas):</strong> Propiedades con más visitas recibidas.</li>
              <li><strong>Top Props (aprobación):</strong> Propiedades mejor calificadas por los visitantes.</li>
              <li><strong>Props críticas:</strong> Propiedades con peor aprobación — requieren atención urgente.</li>
              <li><strong>Top Agentes (visitas):</strong> Agentes que más visitas generaron.</li>
              <li><strong>Top Agentes (aprobación):</strong> Agentes cuyas visitas tuvieron mejor recepción.</li>
            </ul>
            <p>💡 <strong>Cómo usarlo:</strong> Usa "Top Props (visitas)" para ver qué propiedades generan más interés. Usa "Props críticas" para identificar propiedades que necesitan mejoras urgentes en precio, presentación o estado.</p>
          </HelpBox>
        </div>
        <div className={styles.tabsContainer}>
          {rankingTabs.map(t => (
            <button key={t.key} className={`${styles.tab} ${activeTab === t.key ? styles.tabActive : ''}`} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>#</th>
                {isPropertyTab ? (
                  <>
                    <th>Propiedad</th><th>Dirección</th><th>Localidad</th><th>Agente</th>
                    <th>Visitas</th><th>Aprobación</th><th>Int. compra</th><th>Segmento</th>
                  </>
                ) : (
                  <>
                    <th>Agente</th><th>Props c/visitas</th><th>Total visitas</th>
                    <th>Aprobación</th><th>Int. compra</th><th>Segmento</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {(rankingData || []).length === 0 ? (
                <tr><td colSpan={isPropertyTab ? 9 : 7} style={{ textAlign: 'center', padding: 24, color: '#6B6B8A' }}>Sin datos</td></tr>
              ) : rankingData.map((item, i) => (
                <tr key={i}>
                  <td><span className={styles.medal}>{medals[i] || i + 1}</span></td>
                  {isPropertyTab ? (
                    <>
                      <td style={{ fontWeight: 500 }}>{(item.title || '').slice(0, 30)}</td>
                      <td>{item.street} {item.number}</td>
                      <td>{item.locality}</td>
                      <td>{item.sellerName}</td>
                      <td style={{ fontWeight: 700 }}>{item.visitsCount}</td>
                      <td>{(item.approvalRate || 0).toFixed(1)}% <span className={`${styles.badge} ${styles[getSegmentClass(item.segmento)]}`}>{item.segmento}</span></td>
                      <td>{(item.intencionCompra || 0).toFixed(1)}%</td>
                      <td><span className={`${styles.badge} ${styles[getSegmentClass(item.segmento)]}`}>{item.segmento}</span></td>
                    </>
                  ) : (
                    <>
                      <td style={{ fontWeight: 500 }}>{item.name}</td>
                      <td>{item.propertiesWithVisits}</td>
                      <td style={{ fontWeight: 700 }}>{item.visitsCount}</td>
                      <td>{(item.approvalRate || 0).toFixed(1)}% <span className={`${styles.badge} ${styles[getSegmentClass(item.segmento)]}`}>{item.segmento}</span></td>
                      <td>{(item.intencionCompra || 0).toFixed(1)}%</td>
                      <td><span className={`${styles.badge} ${styles[getSegmentClass(item.segmento)]}`}>{item.segmento}</span></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* V10: Desempeño por Agente */}
      {(() => {
        const ag = visitasAgentStats?.data;
        const agData = ag?.current || ag;
        const agMeta = agData?.metadata || {};
        const agGlobal = agData?.global || {};
        const agProm = agGlobal.promedios || {};
        const agFunnel = agGlobal.funnel || {};
        const agTemporal = agGlobal.temporal || {};
        const agRankings = agData?.rankings || {};
        const agInsights = agGlobal.insights || {};
        const agDistributions = agData?.distributions || {};
        const agPropsApproval = agDistributions.propertiesByApprovalSegment || {};

        // Externas del agente
        const agTotalInternas = visitasAgentStats?.totalInternas;
        const agTotalExternas = visitasAgentStats?.totalExternas;
        const agExternas = visitasAgentStats?.visitasExternas || {};
        const agExtInteres = agExternas.interes || {};
        const agExtVisitas = agExternas.visitas || [];

        const agRadarValues = [
          agProm.promedioEstado || 0, agProm.promedioUbicacion || 0, agProm.promedioEspacios || 0,
          agProm.promedioCalidadPrecio || 0, agProm.promedioGeneral || 0, agProm.promedioComprar || 0, agProm.promedioVerOtras || 0
        ];
        const agRadarData = {
          labels: radarLabels,
          datasets: [{
            label: agentSearched || 'Agente',
            data: agRadarValues,
            backgroundColor: 'rgba(139, 34, 82, 0.12)',
            borderColor: '#8B2252',
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: '#8B2252'
          }]
        };

        const agDonutData = {
          labels: segLabels,
          datasets: [{ data: segLabels.map(s => agPropsApproval[s] || 0), backgroundColor: segColors, borderWidth: 0, spacing: 2 }]
        };

        const agMonths = (agTemporal.byMonth || []).map(m => m.month);
        const agLineData = {
          labels: agMonths,
          datasets: [
            { label: 'Approval Rate', data: (agTemporal.byMonth || []).map(m => m.approvalRate), borderColor: '#8B2252', backgroundColor: 'rgba(139, 34, 82, 0.08)', fill: true, tension: 0.3, borderWidth: 2.5, yAxisID: 'y' },
            { label: 'Visitas', data: (agTemporal.byMonth || []).map(m => m.totalVisitas), borderColor: '#2E2F8F', borderDash: [4, 3], fill: false, tension: 0.3, borderWidth: 2, yAxisID: 'y1' }
          ]
        };

        const agTopProps = agRankings.propertiesByVisits || [];

        return (
          <div className={styles.section} style={{ borderTop: '3px solid #8B2252' }}>
            <div className={styles.sectionTitleRow}>
              <h3 className={styles.sectionTitle}>🔍 Desempeño por Agente</h3>
              <HelpBox>
                <p>Seleccioná un agente desde el desplegable en la barra de filtros para ver sus estadísticas individuales:</p>
                <ul>
                  <li>KPIs individuales, radar de dimensiones, funnel de conversión.</li>
                  <li>Evolución temporal mensual y ranking de sus propiedades.</li>
                  <li>Podés cambiar de agente en cualquier momento desde el filtro.</li>
                </ul>
              </HelpBox>
            </div>

            {agentSearched && !agData && !estadisticasLoading && (
              <div className={styles.emptyState} style={{ padding: 24 }}>
                <span className={styles.emptyIcon}>🔍</span>
                <span>No se encontraron datos para "{agentSearched}"</span>
              </div>
            )}

            {agData && (
              <>
                <div className={styles.agentResultHeader}>
                  <span className={styles.agentResultName}>{agentSearched}</span>
                  <span className={styles.agentResultMeta}>
                    {agMeta.totalVisitas || 0} visitas internas · {agTotalExternas || 0} externas · {agMeta.totalPropiedadesConVisitas || 0} propiedades
                  </span>
                </div>

                {/* KPIs del agente */}
                <div className={styles.kpiGrid}>
                  {[
                    { label: 'Visitas realizadas', value: agMeta.totalVisitas || 0, icon: '📊', color: '#8B2252' },
                    { label: 'Propiedades visitadas', value: agMeta.totalPropiedadesConVisitas || 0, icon: '🏠', color: '#8B2252' },
                    { label: 'Approval Rate', value: `${(agProm.approvalRate || 0).toFixed(1)}%`, icon: '⭐', color: '#8B2252', segment: getSegmentFromValue(agProm.approvalRate || 0) },
                    { label: 'Intención compra', value: `${(agProm.promedioComprar || 0).toFixed(1)}%`, icon: '🎯', color: '#8B2252', segment: getSegmentFromValue(agProm.promedioComprar || 0) },
                  ].map((k, i) => (
                    <div key={i} className={styles.kpiCard}>
                      <div className={styles.kpiIcon} style={{ background: `${k.color}15` }}>{k.icon}</div>
                      <span className={styles.kpiLabel}>{k.label}</span>
                      <span className={styles.kpiValue}>{k.value}</span>
                      {k.segment && <span className={`${styles.badge} ${styles[getSegmentClass(k.segment)]}`}>{k.segment}</span>}
                    </div>
                  ))}
                </div>

                {/* Radar + Funnel del agente */}
                <div className={styles.chartRow}>
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Radar — {agentSearched}</h4>
                    <div className={styles.chartContainer}>
                      <Radar data={agRadarData} options={radarOpts} />
                    </div>
                    <div className={styles.insightsBox}>
                      {agInsights.strongestDimension && (
                        <span className={`${styles.insightItem} ${styles.trendBadge} ${styles.trendUp}`}>
                          💪 {agInsights.strongestDimension.key} ({agInsights.strongestDimension.value?.toFixed(1)})
                        </span>
                      )}
                      {agInsights.weakestDimension && (
                        <span className={`${styles.insightItem} ${styles.trendBadge} ${styles.trendDown}`}>
                          ⚠️ {agInsights.weakestDimension.key} ({agInsights.weakestDimension.value?.toFixed(1)})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Funnel — {agentSearched}</h4>
                    <div className={styles.funnelContainer}>
                      {[
                        { label: 'Total visitas', count: agFunnel.totalVisitas || 0, rate: 100, color: '#8B2252' },
                        { label: 'Les gustó', count: agFunnel.gustoPositivo?.count || 0, rate: agFunnel.gustoPositivo?.rate || 0, color: '#A93D6E' },
                        { label: 'Intención compra', count: agFunnel.intencionCompraDirecta?.count || 0, rate: agFunnel.intencionCompraDirecta?.rate || 0, color: '#27AE60' },
                        { label: 'Interesados alternativas', count: agFunnel.interesadosEnAlternativas?.count || 0, rate: agFunnel.interesadosEnAlternativas?.rate || 0, color: '#C9A84C' },
                      ].map((r, i) => (
                        <div key={i} className={styles.funnelRow}>
                          <span className={styles.funnelLabel}>{r.label}</span>
                          <div className={styles.funnelBarBg}>
                            <div className={styles.funnelBar} style={{ width: `${r.rate}%`, background: r.color }}>{r.count}</div>
                          </div>
                          <span className={styles.funnelValue}>{r.rate.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                    {agFunnel.intencionCompraDirecta?.conversionFromGustoPositivo != null && (
                      <div className={styles.conversionBadge}>
                        🔄 Conversión gusto→compra: {agFunnel.intencionCompraDirecta.conversionFromGustoPositivo.toFixed(1)}%
                      </div>
                    )}
                  </div>
                </div>

                {/* Distribución + Temporal del agente */}
                <div className={styles.chartRow}>
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Props por Segmento — {agentSearched}</h4>
                    <div className={styles.donutWrapper}>
                      <div style={{ maxWidth: 260, margin: '0 auto' }}>
                        <Doughnut data={agDonutData} options={donutOpts} />
                      </div>
                    </div>
                  </div>
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Evolución — {agentSearched}</h4>
                    <div className={styles.chartContainer}>
                      {agMonths.length > 0
                        ? <Line data={agLineData} options={lineOpts} />
                        : <div className={styles.emptyState}>Sin datos temporales</div>}
                    </div>
                  </div>
                </div>

                {/* Top Propiedades del agente */}
                {agTopProps.length > 0 && (
                  <div>
                    <h4 className={styles.sectionTitle}>Top Propiedades — {agentSearched}</h4>
                    <div className={styles.tableContainer}>
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Propiedad</th>
                            <th>Dirección</th>
                            <th>Visitas</th>
                            <th>Aprobación</th>
                            <th>Segmento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agTopProps.map((item, i) => (
                            <tr key={i}>
                              <td><span className={styles.medal}>{medals[i] || i + 1}</span></td>
                              <td style={{ fontWeight: 500 }}>{(item.title || '').slice(0, 30)}</td>
                              <td>{item.street} {item.number}, {item.locality}</td>
                              <td style={{ fontWeight: 700 }}>{item.visitsCount}</td>
                              <td>{(item.approvalRate || 0).toFixed(1)}%</td>
                              <td><span className={`${styles.badge} ${styles[getSegmentClass(item.segmento)]}`}>{item.segmento}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Visitas Externas del agente */}
                {agExtVisitas.length > 0 && (
                  <div style={{ borderTop: '2px solid #C9A84C', marginTop: 20, paddingTop: 16 }}>
                    <div className={styles.sectionTitleRow}>
                      <h4 className={styles.sectionTitle}>🌐 Visitas Externas — {agentSearched}</h4>
                    </div>
                    <div className={styles.kpiGrid}>
                      <div className={styles.kpiCard}>
                        <div className={styles.kpiIcon} style={{ background: '#C9A84C15' }}>🌐</div>
                        <span className={styles.kpiLabel}>Total externas</span>
                        <span className={styles.kpiValue}>{agExternas.total || agExtVisitas.length}</span>
                      </div>
                      <div className={styles.kpiCard}>
                        <div className={styles.kpiIcon} style={{ background: '#27AE6015' }}>👍</div>
                        <span className={styles.kpiLabel}>Le interesa</span>
                        <span className={styles.kpiValue}>{agExtInteres.yes || 0}</span>
                      </div>
                      <div className={styles.kpiCard}>
                        <div className={styles.kpiIcon} style={{ background: '#E74C3C15' }}>👎</div>
                        <span className={styles.kpiLabel}>No le interesa</span>
                        <span className={styles.kpiValue}>{agExtInteres.no || 0}</span>
                      </div>
                      <div className={styles.kpiCard}>
                        <div className={styles.kpiIcon} style={{ background: '#E67E2215' }}>🤔</div>
                        <span className={styles.kpiLabel}>Quizás</span>
                        <span className={styles.kpiValue}>{agExtInteres.maybe || 0}</span>
                      </div>
                    </div>
                    <div className={styles.tableContainer}>
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Visitante</th>
                            <th>Dirección</th>
                            <th>Localidad</th>
                            <th>Inmobiliaria</th>
                            <th>Tipo</th>
                            <th>Precio</th>
                            <th>Interés</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agExtVisitas.map((v, i) => (
                            <tr key={i}>
                              <td>{v.fecha}</td>
                              <td style={{ fontWeight: 500 }}>{v.visitante}</td>
                              <td>{v.direccion}</td>
                              <td>{[v.localidad, v.provincia].filter(Boolean).join(', ')}</td>
                              <td>{v.inmobiliariaExterna || '—'}</td>
                              <td>{v.tipoPropiedad || '—'}</td>
                              <td>{v.precio ? `${v.moneda || ''} ${v.precio}` : '—'}</td>
                              <td>
                                {v.interes?.yes && <span style={{ color: '#27AE60', fontWeight: 600 }}>Sí</span>}
                                {v.interes?.no && <span style={{ color: '#E74C3C', fontWeight: 600 }}>No</span>}
                                {v.interes?.maybe && <span style={{ color: '#E67E22', fontWeight: 600 }}>Quizás</span>}
                                {!v.interes?.yes && !v.interes?.no && !v.interes?.maybe && '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Modal detalle de segmento */}
      {segmentModal && (
        <div className={styles.modalOverlay} onClick={() => setSegmentModal(null)}>
          <div className={styles.segmentModalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.segmentModalHeader}>
              <div className={styles.segmentModalTitle}>
                <span className={styles.segmentModalDot} style={{ background: segmentModal.color }} />
                {segmentModal.type === 'properties' ? 'Propiedades' : 'Agentes'} — Segmento {segmentModal.segment}
              </div>
              <button className={styles.segmentModalClose} onClick={() => setSegmentModal(null)}>✕</button>
            </div>
            <div className={styles.segmentModalCount}>
              {segmentModal.items.length} {segmentModal.type === 'properties' ? 'propiedades' : 'agentes'} en este segmento
            </div>

            {segmentModal.items.length === 0 ? (
              <div className={styles.segmentModalEmpty}>No hay datos en este segmento</div>
            ) : segmentModal.type === 'properties' ? (
              <table className={styles.segmentModalTable}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Ubicación</th>
                    <th>Visitas</th>
                    <th>Aprobación</th>
                    <th>Agente</th>
                  </tr>
                </thead>
                <tbody>
                  {segmentModal.items.map(p => (
                    <tr key={p.propertyId}>
                      <td>{p.propertyId}</td>
                      <td style={{ fontWeight: 500 }}>{(p.title || '').slice(0, 35)}</td>
                      <td>{[p.street, p.number, p.locality].filter(Boolean).join(', ')}</td>
                      <td>{p.visitsCount}</td>
                      <td>
                        {(p.approvalRate || 0).toFixed(1)}%
                        <span className={`${styles.badge} ${styles[getSegmentClass(p.segmento)]}`} style={{ marginLeft: 6 }}>{p.segmento}</span>
                      </td>
                      <td>{p.sellerName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className={styles.segmentModalTable}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Agente</th>
                    <th>Props c/visitas</th>
                    <th>Total visitas</th>
                    <th>Aprobación</th>
                  </tr>
                </thead>
                <tbody>
                  {segmentModal.items.map(s => (
                    <tr key={s.sellerId}>
                      <td>{s.sellerId}</td>
                      <td style={{ fontWeight: 500 }}>{s.name}</td>
                      <td>{s.propertiesWithVisits}</td>
                      <td>{s.visitsCount}</td>
                      <td>
                        {(s.approvalRate || 0).toFixed(1)}%
                        <span className={`${styles.badge} ${styles[getSegmentClass(s.segmento)]}`} style={{ marginLeft: 6 }}>{s.segmento}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitasStats;
